import React, { useCallback } from 'react';

import * as Sentry from '@sentry/nextjs';
import PropTypes from 'prop-types';
import { FaRegHeart, FaHeart } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { useWishlistIds } from '@techstyle/react-accounts';
import {
  WishlistToggleButton as BaseWishlistToggleButton,
  desktop,
  mobile,
} from '@techstyle/react-components';
import { useIntl } from '@techstyle/react-intl';
import { useSession, useSessionActions } from '@techstyle/redux-core';

import { toggleSignInModal } from '../../actions/auth';
import { SignUpMethod } from '../../constants/auth';
import { isProductSet } from '../../utils/selectors';
import { Component as BorderfreeGuard } from '../BorderfreeGuard';

const WishlistToggleButton = styled(BaseWishlistToggleButton)`
  background-color: transparent;

  ${mobile`
    padding: 4px;
    font-size: 18px;
  `}
  ${desktop`
    padding: 4px;
    font-size: 18px;
  `}
`;

const EmptyHeartIcon = styled(FaRegHeart).attrs(props => ({
  color: props.theme.colors.default,
}))``;

const HeartIcon = styled(FaHeart).attrs(props => ({
  color: props.theme.colors.default,
}))``;

const FavoritesToggleButton = ({ masterProductId }) => {
  const dispatch = useDispatch();
  const { isLoggedIn } = useSession();
  const wishlistIds = useWishlistIds();
  const sessionActions = useSessionActions();
  const inWishlist = wishlistIds.hasProductId(masterProductId);
  const intl = useIntl();

  const handleAddToFavorites = useCallback(
    async ({ product, addToWishlist }) => {
      if (isLoggedIn) {
        addToWishlist();
      } else {
        try {
          sessionActions.updateSessionDetail({
            name: 'signup_favorites',
            value: '',
          });

          const data = await dispatch(
            toggleSignInModal(
              true,
              'SIGN_UP',
              SignUpMethod.WISHLIST,
              product.masterProductId,
              isProductSet(product)
            )
          );
          if (data && data.signedIn) {
            addToWishlist();
          }
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    },
    [isLoggedIn, dispatch]
  );

  return (
    <BorderfreeGuard>
      <WishlistToggleButton
        data-qa-automation={
          inWishlist ? 'gridItemWishlistIconFilled' : 'gridItemWishlistIcon'
        }
        onAdd={handleAddToFavorites}
        aria-label={
          inWishlist
            ? intl.formatMessage({
                id: 'global_cta.wishlist_saved',
                defaultMessage: 'Remove from Favorites',
              })
            : intl.formatMessage({
                id: 'global_cta.wishlist_add',
                defaultMessage: 'Add to Favorites',
              })
        }
        addLabel={<EmptyHeartIcon />}
        removeLabel={<HeartIcon />}
      />
    </BorderfreeGuard>
  );
};

FavoritesToggleButton.propTypes = {
  masterProductId: PropTypes.string,
};

export default FavoritesToggleButton;
